import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCaseCategories, getCases } from "@/api/caseApi";
import { assetUrl } from "@/utils/assetUrl";
import { useSEO, buildBreadcrumbLd } from "@/utils/seo";
import { readPreload, buildPreloadKey } from "@/api/preload";
import { CASE_CATEGORY_STYLE, getCaseCategoryStyle } from "@/utils/solutionDisplay";



interface CaseCategory { id: string; name: string; }
interface CaseMetric { value: string; label: string; }
interface CaseItem {
  id: number;
  solution: string;
  solutionName: string;
  client: string;
  title: string;
  summary: string;
  desc: string;
  tags: string[];
  metrics: CaseMetric[];
  image: string;
}

/** 派生 `caseList` 状态：与 `fetchData()` 内既有的字段归一逻辑保持一致 */
function deriveCaseList(rowsRaw: any): CaseItem[] {
  const rows = rowsRaw?.rows ?? rowsRaw?.data ?? (Array.isArray(rowsRaw) ? rowsRaw : []);
  return rows.map((c: any) => ({
    ...c,
    tags: c.tags ?? [],
    metrics: c.metrics ?? [],
    summary: c.summary ?? c.description ?? "",
    solution: (c.solution ?? "").trim(),
    solutionName: c.solutionName ?? "",
  }));
}

/** 派生 `categories` 状态：与 `fetchData()` 内既有的「全部案例」兜底追加逻辑保持一致 */
function deriveCategories(catResRaw: any): CaseCategory[] {
  const cats: CaseCategory[] = Array.isArray(catResRaw) ? catResRaw : catResRaw?.data ?? [];
  return cats.some((c) => c.id === "all") ? cats : [{ id: "all", name: "全部案例" }, ...cats];
}

const CASES_PRELOAD_KEY = buildPreloadKey("GET", "/api/front/cases", { pageSize: 100 });
const CASE_CATEGORIES_PRELOAD_KEY = buildPreloadKey("GET", "/api/front/cases/categories");

export default function CasesPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [categories, setCategories] = useState<CaseCategory[]>(() => {
    const preloaded = readPreload<any>(CASE_CATEGORIES_PRELOAD_KEY);
    return preloaded !== undefined ? deriveCategories(preloaded) : [];
  });
  const [caseList, setCaseList] = useState<CaseItem[]>(() => {
    const preloaded = readPreload<any>(CASES_PRELOAD_KEY);
    return preloaded !== undefined ? deriveCaseList(preloaded) : [];
  });
  const [loading, setLoading] = useState(
    () => readPreload<any>(CASES_PRELOAD_KEY) === undefined || readPreload<any>(CASE_CATEGORIES_PRELOAD_KEY) === undefined,
  );
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useSEO({
    title: "成功案例 | 500+ 行业头部客户的安全实战",
    description:
      "欧格登成功案例库，覆盖金融、政府、教育、医疗、制造等行业的信息安全、IT基础架构、云信达与AI业务项目落地实践，真实数据与客户证言说话。",
    keywords: ["欧格登案例", "信息安全案例", "IT基础架构案例", "云信达案例", "AI业务案例", "企业安全实战"],
    path: "/cases",
    pageType: "CASE_LIST",
    jsonLd: buildBreadcrumbLd([
      { name: "首页", path: "/" },
      { name: "成功案例", path: "/cases" },
    ]),
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [catRes, casesRes] = await Promise.all([
          getCaseCategories(),
          getCases({ pageSize: 100 }),
        ]);
        // 后端已包含"全部案例"，直接使用，不重复添加
        setCategories(deriveCategories(catRes));
        setCaseList(deriveCaseList(casesRes));
      } catch (e: any) {
        console.error("加载案例数据失败", e);
        setError("加载案例数据失败，请稍后重试");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const solution = searchParams.get("solution");
    if (solution && categories.length > 0) {
      const validIds = categories.map(c => c.id);
      if (validIds.includes(solution)) {
        setActiveCategory(solution);
      }
    }
  }, [searchParams, categories]);

  const filtered = activeCategory === "all"
    ? caseList
    : caseList.filter((c) => c.solution === activeCategory);

  // “全部案例”用中性灰；已配置的案例分类取统一样式；未配置分类用醒目的回退色，便于发现遗漏配置
  const getActiveBtnColor = (catId: string) =>
    catId === "all" ? "#6b7280" : CASE_CATEGORY_STYLE[catId]?.hex || "#b45309";

  const getCategoryStyle = (solution: string) => getCaseCategoryStyle(solution);

  // 文案与统计随案例分类/数据动态生成，不再写死领域数量
  const realCategories = categories.filter((c) => c.id !== "all");
  const categoryNames = realCategories.map((c) => c.name).join("、");

  return (
    <main className="min-h-screen bg-[#f8fafc]">
      {/* Hero */}
      <section className="relative bg-[#0d1f3c] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=enterprise%20success%20stories%20business%20achievements%2C%20professional%20corporate%20case%20studies%20background%2C%20dark%20blue%20technology%20atmosphere%2C%20business%20excellence%20visualization&width=1920&height=500&seq=casehero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/60 to-[#0d1f3c]"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full mb-5">
            <span className="text-white/80 text-xs font-medium">成功案例</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            服务 <span style={{ color: "#fbbf24" }}>500+</span> 行业头部企业
          </h1>
          <p className="text-gray-400 text-sm lg:text-base max-w-2xl mx-auto">
            覆盖{categoryNames || "信息安全、基础架构、云信达、AI业务"}等领域，积累丰富的标杆案例
          </p>
          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-10">
            {[
              { value: "500+", label: "服务客户" },
              { value: caseList.length > 0 ? String(caseList.length) : "-", label: "典型案例" },
              { value: realCategories.length > 0 ? String(realCategories.length) : "-", label: "核心领域" },
              { value: "10年+", label: "行业经验" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-2xl lg:text-3xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-gray-400 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <div className="bg-[#f8fafc] border-b border-gray-100 sticky top-16 lg:top-20 z-30">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map((cat) => {
              const isActive = activeCategory === cat.id;
              const activeColor = getActiveBtnColor(cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                  style={isActive
                    ? { backgroundColor: activeColor, color: "#fff", borderColor: activeColor }
                    : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                  }
                >
                  {cat.name}
                  <span
                    className="ml-1.5 text-xs px-1.5 py-0.5 rounded-full"
                    style={isActive
                      ? { backgroundColor: "rgba(255,255,255,0.2)", color: "#fff" }
                      : { backgroundColor: "#f3f4f6", color: "#9ca3af" }
                    }
                  >
                    {cat.id === "all" ? caseList.length : caseList.filter(c => c.solution === cat.id).length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Cases Grid */}
      <section className="py-10 lg:py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="py-20 text-center text-gray-400 text-sm">加载中...</div>
          ) : error ? (
            <div className="py-20 text-center text-red-400 text-sm">{error}</div>
          ) : activeCategory === "all" ? (
            /* Grouped view when showing all */
            <div className="space-y-14">
              {categories.filter(c => c.id !== "all").map((cat) => {
                const catCases = caseList.filter(c => c.solution === cat.id);
                if (catCases.length === 0) return null;
                const style = getCategoryStyle(cat.id);
                return (
                  <div key={cat.id}>
                    <div className="flex items-center gap-3 mb-6">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: `${getCategoryStyle(cat.id).hex}12`, color: getCategoryStyle(cat.id).hex, border: `1px solid ${getCategoryStyle(cat.id).hex}30` }}
                      >
                        {cat.name}
                      </span>
                      <div className="flex-1 h-px bg-gray-100"></div>
                      <span className="text-xs text-gray-400">{catCases.length} 个案例</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {catCases.map((c) => (
                        <CaseCard key={c.id} c={c} onClick={() => navigate(`/cases/${c.id}`)} style={style} />
                      ))}
                    </div>
                  </div>
                );
              })}
              {/* 未分类的案例 */}
              {(() => {
                const knownIds = new Set(categories.filter(c => c.id !== "all").map(c => c.id));
                const uncategorized = caseList.filter(c => !c.solution || !knownIds.has(c.solution));
                if (uncategorized.length === 0) return null;
                const style = { bg: "bg-gray-50", text: "text-gray-600", border: "border-gray-100", hex: "#6b7280" };
                return (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-500 border border-gray-200">
                        其他
                      </span>
                      <div className="flex-1 h-px bg-gray-100"></div>
                      <span className="text-xs text-gray-400">{uncategorized.length} 个案例</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {uncategorized.map((c) => (
                        <CaseCard key={c.id} c={c} onClick={() => navigate(`/cases/${c.id}`)} style={style} />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            /* Flat grid when filtered */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((c) => {
                const style = getCategoryStyle(c.solution);
                return <CaseCard key={c.id} c={c} onClick={() => navigate(`/cases/${c.id}`)} style={style} />;
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#F4F7FC]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-4">成为我们的下一个成功案例</h2>
          <p className="text-sm mb-7 text-[#555555]">联系我们，获取专属解决方案</p>
          <a
            href="/#contact-form"
            className="inline-block px-10 py-3.5 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
            style={{ backgroundColor: "#1a56db" }}
          >
            立即咨询
          </a>
        </div>
      </section>
    </main>
  );
}

interface CaseCardProps {
  c: CaseItem;
  onClick: () => void;
  style: { bg: string; text: string; border: string };
}

function CaseCard({ c, onClick, style }: CaseCardProps) {
  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    >
      <div className="w-full h-48 overflow-hidden">
        <img
          src={assetUrl(c.image)}
          alt={c.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${style.bg} ${style.text} border ${style.border}`}>
            {c.solutionName}
          </span>
          <span className="text-gray-400 text-xs truncate">{c.client}</span>
        </div>
        <h3 className="text-sm font-bold text-[#0d1f3c] mb-2 leading-snug line-clamp-2">{c.title}</h3>
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 mb-4">{c.summary}</p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {c.tags.map((tag) => (
            <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-400 text-xs rounded-full border border-gray-100">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex gap-5 pt-3 border-t border-gray-50">
          {c.metrics.map((m) => (
            <div key={m.label}>
              <div className="text-base font-bold text-[#0d1f3c]">{m.value}</div>
              <div className="text-xs text-gray-400">{m.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
